import { formatRelativeAgo } from './intlFormatters';

// Helper to format relative time (localized via dates namespace)
export const formatRelativeTime = (dateString) => formatRelativeAgo(dateString);

// Helper to format file size in human-readable format
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// The error text a failed statement process can show, or null when it carries
// no error. A failure arrives with a null message and an error_code — its raw
// text can hold the very rows being imported, so it stays in the server logs —
// and gets a translated message instead: 'import_interrupted' when the run was
// cut short (a server restart) and could not be resumed, 'ai_budget_reached'
// when the uploader's AI limit was reached before it started (MI-354),
// 'import_failed' for everything else. A message, if the backend ever sends
// one, is shown as is.
// `t` is the sourceFiles translator.
export const processErrorText = (process, t) => {
  const message = process?.error_message ||
    (typeof process?.error === 'object' ? process?.error?.message : process?.error);
  if (message) return message;
  const code = process?.error_code || process?.error?.error_code;
  if (code === 'import_interrupted') return t('pipeline.importInterrupted');
  if (code === 'ai_budget_reached') return t('pipeline.aiBudgetReached');
  return code === 'import_failed' ? t('pipeline.importFailed') : null;
};

// File type detection helper
export const getFileType = (filename) => {
  if (!filename) return null;
  const ext = filename.split('.').pop().toLowerCase();
  if (['csv', 'txt', 'tsv'].includes(ext)) return 'csv';
  if (['pdf'].includes(ext)) return 'pdf';
  if (['xlsx', 'xls'].includes(ext)) return 'excel';
  return 'unknown';
};

// Check if file type requires source (PDF/Excel) or account (CSV)
export const requiresSource = (fileType) => ['pdf', 'excel'].includes(fileType);

// Get step status from process state
export const getStepStatus = (processState, stepNumber) => {
  const stepData = processState.stepsSummary?.find(s => s.step_number === stepNumber);
  if (!stepData) {
    if (processState.currentStep > stepNumber) return 'completed';
    if (processState.currentStep === stepNumber && processState.status === 'running') return 'running';
    return 'waiting';
  }
  return stepData.status;
};

// Get step result summary text
export const getStepResultText = (processState, stepNumber) => {
  const stepData = processState.stepsSummary?.find(s => s.step_number === stepNumber);
  if (!stepData?.results) return null;

  const { transaction: tx, movement: mv } = stepData.results;
  switch (stepNumber) {
    case 1:
      return `${tx.new} new, ${tx.existing} existing`;
    case 2:
      return `${tx.matched} matched`;
    case 3:
      return `${tx.matched} relinked`;
    case 4:
      return `${mv.created.count} movements`;
    case 5:
      return `${mv.created.count} categorized`;
    default:
      return null;
  }
};
